import React from 'react';
import { Radio, Progress } from 'antd';
import {
  CalendarOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  DollarOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
  FileTextOutlined,
  DownloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import './VCCDashboard.scss';

// Reusable MetricCard Component
const MetricCard = ({ title, value, subtitle, icon, trend, details, variant = 'default' }) => {
  return (
    <div className={`metric-card ${variant}`}>
      <div className="metric-header">
        <h3 className="metric-title">{title}</h3>
        <div className={`metric-icon ${variant}`}>{icon}</div>
      </div>
      <div className="metric-value">{value}</div>
      {trend && (
        <div className="metric-trend">
          <span className="trend-arrow">↗</span>
          <span className="trend-value">{trend.value}</span>
          <span className="trend-label">{trend.label}</span>
        </div>
      )}
      {subtitle && <div className="metric-subtitle">{subtitle}</div>}
      {details && (
        <div className="metric-details">
          {details.map((detail, idx) => (
            <div key={idx} className="detail-row">
              <span className="detail-label">{detail.label}</span>
              <span className="detail-value">{detail.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Reusable DetailList Component
const DetailList = ({ items }) => {
  return (
    <div className="detail-list">
      {items.map((item, idx) => (
        <div key={idx} className="detail-item">
          <div className="detail-indicator" style={{ backgroundColor: item.color }} />
          <span className="detail-text">{item.label}</span>
          <span className="detail-count">({item.count})</span>
          <span className="detail-amount">{item.amount}</span>
        </div>
      ))}
    </div>
  );
};

// Reusable StatusCard Component
const StatusCard = ({ type, data }) => {
  const getStatusColor = (type) => {
    const colors = {
      success: '#52c41a',
      warning: '#faad14',
      error: '#ff4d4f',
    };
    return colors[type] || '#d9d9d9';
  };

  return (
    <div className={`status-card status-${type}`}>
      <div className="status-time">{data.time}</div>
      <div className="status-id">{data.id}</div>
      <div className="status-location">{data.location}</div>
      <div className="status-amount">{data.amount}</div>
      <div className="status-message">{data.message}</div>
    </div>
  );
};

// Reusable ActionCard Component
const ActionCard = ({ icon, title, subtitle, onClick, variant = 'default' }) => {
  return (
    <div className={`action-card action-${variant}`} onClick={onClick}>
      <div className={`action-icon ${variant}`}>{icon}</div>
      <div className="action-content">
        <h4 className="action-title">{title}</h4>
        <p className="action-subtitle">{subtitle}</p>
      </div>
    </div>
  );
};

// Main VCC Dashboard Component
const VCCDashboard = () => {
  const [timePeriod, setTimePeriod] = React.useState('month');

  const pendingChargesDetails = [
    { label: '0-7 days', count: 98, amount: '₹7,45,230', color: '#52c41a' },
    { label: '8-15 days', count: 45, amount: '₹4,32,150', color: '#faad14' },
    { label: '15+ days', count: 23, amount: '₹2,47,558', color: '#ff4d4f' },
  ];

  const atRiskDetails = [
    { label: 'Double Payment Recovery', count: 68, amount: '₹6,07,555', color: '#ff4d4f' },
    { label: 'Potential Chargebacks', count: 7, amount: '₹96,445', color: '#ff4d4f' },
    { label: 'Expired VCCs', count: 32, amount: '₹2,33,675', color: '#faad14' },
  ];

  const hotelDetails = [
    { label: 'VCC Bookings', count: '2,847 (+12.5%)', amount: '', color: '#52c41a' },
    { label: 'VCC Booking Amount', count: '', amount: '₹2,45,67,890', color: '#1890ff' },
    { label: 'Non-VCC Bookings', count: '8,451 (-12.5%)', amount: '', color: '#faad14' },
    { label: 'Non-VCC Amount', count: '', amount: '₹7,83,45,220', color: '#8c8c8c' },
  ];

  const revenueDetails = [
    { label: 'Upcoming Potential Cashback', count: '', amount: '₹7,125', color: '#faad14' },
    { label: 'Payments Processed (NEFT/RTGS)', count: '', amount: '₹7,83,45,220', color: '#52c41a' },
    { label: 'Breakage amount', count: '', amount: '₹1,17,600', color: '#722ed1' },
  ];

  const recentActivities = [
    {
      type: 'success',
      data: {
        time: '2 min ago',
        id: 'BK12847',
        location: 'Taj Palace Mumbai',
        amount: '₹8,450',
        message: 'VCC charged successfully',
      },
    },
    {
      type: 'warning',
      data: {
        time: '15 min ago',
        id: 'BK12846',
        location: 'ITC Grand Goa',
        amount: '₹12,300',
        message: 'OTP sent to hotel',
      },
    },
    {
      type: 'error',
      data: {
        time: '32 min ago',
        id: 'BK12845',
        location: 'Oberoi Udaipur',
        amount: '₹15,600',
        message: 'OTP delivery failed',
      },
    },
  ];

  const liveStatusData = {
    charged: 2681,
    issued: 98,
    otpSent: 45,
    failed: 15,
    expired: 8,
  };

  const total = Object.values(liveStatusData).reduce((a, b) => a + b, 0);
  const chargedPercent = (liveStatusData.charged / total) * 100;

  return (
    <div className="vcc-dashboard">
      {/* Time Period Selector */}
      <div className="period-selector">
        <CalendarOutlined className="period-icon" />
        <span className="period-label">Time Period:</span>
        <Radio.Group value={timePeriod} onChange={(e) => setTimePeriod(e.target.value)}>
          <Radio.Button value="day">Day</Radio.Button>
          <Radio.Button value="week">Week</Radio.Button>
          <Radio.Button value="month">Month</Radio.Button>
          <Radio.Button value="custom">Custom</Radio.Button>
        </Radio.Group>
        <span className="period-display">Showing data for: <strong>Month</strong></span>
      </div>

      {/* Top Metrics Row */}
      <div className="metrics-row">
        <MetricCard
          title="Total VCCs Issued"
          value="2,847"
          icon={<CreditCardOutlined />}
          trend={{ value: '+12.5%', label: 'vs last month' }}
          details={[{ label: 'Total Amount', value: '₹2,45,67,890' }]}
        />
        <MetricCard
          title="VCCs Charged Successfully"
          value="94.2%"
          subtitle="2,681 of 2,847"
          icon={<CheckCircleOutlined />}
          trend={{ value: '+2.1%', label: '' }}
          details={[{ label: 'Total Amount', value: '₹2,31,42,952' }]}
          variant="success"
        />
        <MetricCard
          title="Pending Charges"
          value="166"
          icon={<ClockCircleOutlined />}
          details={[
            { label: 'Total Amount', value: '₹14,24,938' },
            ...pendingChargesDetails.map(d => ({ label: d.label, value: `${d.count} - ${d.amount}` }))
          ]}
          variant="warning"
        />
      </div>

      {/* Middle Metrics Row */}
      <div className="metrics-row">
        <MetricCard
          title="At Risk Amount"
          value="₹9,37,675"
          icon={<ExclamationCircleOutlined />}
          variant="danger"
          details={atRiskDetails.map(d => ({ label: `${d.label} (${d.count})`, value: d.amount }))}
        />
        <MetricCard
          title="Unique Hotels"
          value="1,245"
          icon={<BankOutlined />}
          details={hotelDetails.map(d => ({ 
            label: d.label, 
            value: d.amount || d.count 
          }))}
        />
        <MetricCard
          title="VCC Revenue"
          value="₹2,42,280"
          icon={<DollarOutlined />}
          trend={{ value: '+18.5%', label: 'vs last month' }}
          details={revenueDetails.map(d => ({ label: d.label, value: d.amount }))}
          variant="info"
        />
      </div>

      {/* Live Status and Recent Activity */}
      <div className="status-row">
        <div className="live-status-card">
          <div className="status-header">
            <h3>Live VCC Status Monitor</h3>
            <span className="realtime-badge">
              <span className="realtime-dot" />
              Real-time
            </span>
          </div>
          <div className="status-chart">
            <Progress
              type="circle"
              percent={chargedPercent}
              strokeColor="#52c41a"
              trailColor="#f0f0f0"
              format={() => ''}
              width={280}
              strokeWidth={12}
            />
          </div>
          <div className="status-legend">
            <div className="legend-row">
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: '#52c41a' }} />
                <span className="legend-label">Charged</span>
                <span className="legend-value">{liveStatusData.charged}</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: '#1890ff' }} />
                <span className="legend-label">Issued</span>
                <span className="legend-value">{liveStatusData.issued}</span>
              </div>
            </div>
            <div className="legend-row">
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: '#13c2c2' }} />
                <span className="legend-label">OTP Sent</span>
                <span className="legend-value">{liveStatusData.otpSent}</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: '#ff4d4f' }} />
                <span className="legend-label">Failed</span>
                <span className="legend-value">{liveStatusData.failed}</span>
              </div>
            </div>
            <div className="legend-row">
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: '#8c8c8c' }} />
                <span className="legend-label">Expired</span>
                <span className="legend-value">{liveStatusData.expired}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="recent-activity-card">
          <h3>Recent Activity</h3>
          <div className="activity-list">
            {recentActivities.map((activity, idx) => (
              <StatusCard key={idx} type={activity.type} data={activity.data} />
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <ActionCard
            icon={<PlusOutlined />}
            title="Generate New VCC"
            subtitle="Create single VCC"
            variant="primary"
          />
          <ActionCard
            icon={<SearchOutlined />}
            title="Find VCC"
            subtitle="Search and view details"
            variant="success"
          />
          <ActionCard
            icon={<UploadOutlined />}
            title="Bulk VCC Upload"
            subtitle="Upload CSV/Excel"
            variant="warning"
          />
          <ActionCard
            icon={<FileTextOutlined />}
            title="View Pending Reconciliations"
            subtitle="23 items pending"
            variant="danger"
          />
          <ActionCard
            icon={<DownloadOutlined />}
            title="Download Reports"
            subtitle="Export data"
            variant="info"
          />
          <ActionCard
            icon={<SyncOutlined />}
            title="Configuration"
            subtitle="System settings"
            variant="secondary"
          />
        </div>
      </div>
    </div>
  );
};

export default VCCDashboard; 